import { FAQ_CONTENT, LEGAL_CONTENT } from './legal-content.generated';
import { STAYS_LEGAL_CONTENT } from './stays-legal-content';

/* The legal pages the app carries, plus the FAQ, in the order every list of
   them uses. The TEXT is not written here. It is copied from lobbero_web by
   scripts/sync-legal.mjs (see legal-content.generated.ts). This file only
   decides what each one is called in a short link.

   The two Lobbero Stays documents (Independent Stays IS3) are the exception:
   drafts written in the app (stays-legal-content.ts), opened from the Stays
   sign-up, and listed only beside each other — never in the general rows
   (sign-in footer, Account › Legal), which stay the Lobbero set. */
export type PolicySlug = keyof typeof LEGAL_CONTENT;
export type StaysLegalSlug = keyof typeof STAYS_LEGAL_CONTENT;
export type LegalSlug = PolicySlug | 'faq' | StaysLegalSlug;

export interface LegalDoc {
  slug: LegalSlug;
  /** Short name for a link row ("Privacy"). */
  short: string;
  /** The document's own title, from its <h1>. */
  title: string;
  description: string;
}

const SHORT: Record<PolicySlug | 'faq', string> = {
  privacy: 'Privacy',
  terms: 'Terms',
  cookies: 'Cookies',
  dpa: 'DPA',
  security: 'Security',
  faq: 'FAQ',
};

export const LEGAL_DOCS: LegalDoc[] = (Object.keys(SHORT) as (PolicySlug | 'faq')[]).map(
  slug => {
    const source = slug === 'faq' ? FAQ_CONTENT : LEGAL_CONTENT[slug];

    return {
      slug,
      short: SHORT[slug],
      title: source.title,
      description: source.description,
    };
  }
);

const STAYS_SHORT: Record<StaysLegalSlug, string> = {
  'stays-terms': 'Stays terms',
  'stays-dpa': 'Stays DPA',
};

export const STAYS_LEGAL_DOCS: LegalDoc[] = (Object.keys(STAYS_SHORT) as StaysLegalSlug[]).map(slug => ({
  slug,
  short: STAYS_SHORT[slug],
  title: STAYS_LEGAL_CONTENT[slug].title,
  description: STAYS_LEGAL_CONTENT[slug].description,
}));

export const isStaysLegal = (slug: LegalSlug): slug is StaysLegalSlug =>
  Object.prototype.hasOwnProperty.call(STAYS_LEGAL_CONTENT, slug);

export const legalHref = (slug: LegalSlug): string => `/legal/${slug}`;

export const findLegalDoc = (slug: string | undefined): LegalDoc | undefined =>
  LEGAL_DOCS.find(doc => doc.slug === slug) || STAYS_LEGAL_DOCS.find(doc => doc.slug === slug);

/** The policy HTML for a slug, or undefined for the FAQ (rendered from data). */
export const policyHtml = (slug: LegalSlug): string | undefined => {
  if (slug === 'faq') return undefined;
  if (isStaysLegal(slug)) return STAYS_LEGAL_CONTENT[slug].html;

  return LEGAL_CONTENT[slug].html;
};
